import { useCallback, useMemo, useRef, useState } from 'react';
import { KnownHubDetailsDtoType, TowerModelDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { CustomValidator } from '@rjsf/utils';
import {
  useMutationSetupExternalLockersHub,
  useMutationSetupLockersHub,
  useMutationUpdateLockersHubSetup,
  useQueryGetHubDetails
} from '../../../lockers/hubs.queries';
import { useQueryListTowerModels } from '../../../lockers/lockers.queries';
import { EntityForm } from '../form.types';
import { HubLayoutForm, HubLayoutTowerRow, LockedKiosk } from './form';
import { HubLayoutFormOutput } from './form-output';
import { HubLayoutFormSchema, getManufacturerForHubType } from './form-schema';
import { buildHubLayoutFormUISchema } from './ui-schema';
import { useMutationUpdateHub } from '../../../lockers/hubs.queries';
import { useHasDoubleSidedTower } from '../../../lockers/list/hooks/use-has-double-sided-tower.hook';
import { HubLayoutTowersField } from './hub-layout-towers-field';
import { HubLayoutPreview } from '../shared/hub-layout-preview.component';

function findKioskTower(towers: HubLayoutTowerRow[], towerModels: TowerModelDto[]): LockedKiosk | undefined {
  const kiosk = towers.find((t) => towerModels.find((m) => m.name === t.model)?.kioskPosition !== undefined);
  return kiosk?.model && kiosk.inventoryID ? { model: kiosk.model, inventoryID: kiosk.inventoryID } : undefined;
}

export function useEditHubLayoutForm(
  hubID: string | undefined,
  formData: HubLayoutForm | undefined
): EntityForm<HubLayoutForm, HubLayoutFormOutput> | null {
  const hubDetailsResult = useQueryGetHubDetails(hubID);
  const setupMutation = useMutationSetupLockersHub();
  const setupExternalMutation = useMutationSetupExternalLockersHub();
  const updateMutation = useMutationUpdateLockersHubSetup();
  const updateHubMutation = useMutationUpdateHub();
  const allTowerModelsResult = useQueryListTowerModels();

  const serverIsSetupComplete = (hubDetailsResult.data?.towers?.length ?? 0) > 0;

  // Captured immediately after first save (avoids waiting for refetch).
  // Scoped to hubID so navigating to another hub doesn't carry over the saved state.
  const [savedKioskState, setSavedKioskState] = useState<{ hubID: string; kiosk: LockedKiosk } | undefined>(undefined);
  const [sidesWarning, setSidesWarning] = useState<string | null>(null);
  const savedKiosk = savedKioskState && savedKioskState.hubID === hubID ? savedKioskState.kiosk : undefined;

  const defaultManufacturer = getManufacturerForHubType(hubDetailsResult.data?.type);
  const hubType = hubDetailsResult.data?.type;
  const isExternalHub = hubType === KnownHubDetailsDtoType.LockersHubExternal;

  const defaultValues: HubLayoutForm = useMemo(() => {
    if (!hubDetailsResult.data) {
      return {
        manufacturer: defaultManufacturer,
        iotHubID: undefined,
        sidesSelection: 'single',
        towers: [{ model: '', inventoryID: '' }]
      };
    }
    const form = HubLayoutFormOutput.FromHubDetails(hubDetailsResult.data);
    const loadedModelNames = (form.towers ?? []).map((t) => t.model).filter(Boolean);
    const allTowerModels = allTowerModelsResult.data ?? [];
    const initialIsDouble =
      allTowerModels.length > 0 &&
      loadedModelNames.some((name) => allTowerModels.find((m) => m.name === name)?.isDoubleSided);
    return {
      name: form.name,
      manufacturer: form.manufacturer ?? defaultManufacturer,
      iotHubID: form.iotHubID,
      sidesSelection: initialIsDouble ? 'double' : 'single',
      towers: form.towers,
      frontName: form.frontName,
      backName: form.backName
    };
  }, [hubDetailsResult.data, defaultManufacturer, allTowerModelsResult.data]);

  const initialFormOutput = useMemo(() => {
    if (!hubDetailsResult.data) return new HubLayoutFormOutput({ towers: [] });
    return new HubLayoutFormOutput(HubLayoutFormOutput.FromHubDetails(hubDetailsResult.data));
  }, [hubDetailsResult.data]);

  const towerModelNames = useMemo(() => {
    const formTowers: HubLayoutTowerRow[] = formData?.towers ?? defaultValues?.towers ?? [];
    return formTowers.map((t) => t.model).filter(Boolean);
  }, [formData, defaultValues?.towers]);

  const { hasDoubleSided, towerModels } = useHasDoubleSidedTower(towerModelNames);

  const initialTowers = useMemo(() => {
    if (!hubDetailsResult.data) return undefined;
    return HubLayoutFormOutput.FromHubDetails(hubDetailsResult.data).towers;
  }, [hubDetailsResult.data]);

  // Latching ref: once we resolve the locked kiosk from server data, never go back to undefined.
  // Scoped to hubID so navigating to another hub resets the cached value.
  const serverLockedKioskRef = useRef<{ hubID: string | undefined; kiosk: LockedKiosk | undefined }>({
    hubID: undefined,
    kiosk: undefined
  });

  const serverLockedKiosk = useMemo(() => {
    if (serverLockedKioskRef.current.hubID !== hubID) {
      serverLockedKioskRef.current = { hubID, kiosk: undefined };
    }
    if (!serverIsSetupComplete || !initialTowers || towerModels.length === 0) {
      return serverLockedKioskRef.current.kiosk;
    }
    const kiosk = findKioskTower(initialTowers, towerModels);
    serverLockedKioskRef.current = { hubID, kiosk };
    return kiosk;
  }, [hubID, serverIsSetupComplete, initialTowers, towerModels]);

  // Combined: post-save state (immediate) OR server-derived state (on load)
  const lockedKiosk = savedKiosk ?? serverLockedKiosk;
  const isSetupComplete = serverIsSetupComplete || savedKiosk !== undefined;

  // External lockers hubs have no PATCH endpoint — all fields are read-only once setup is complete
  const isExternalSetupLocked = isExternalHub && isSetupComplete;

  const formSchema = useMemo(() => {
    return HubLayoutFormSchema.FromFormData(
      towerModels,
      hubType,
      hasDoubleSided,
      isSetupComplete,
      isExternalSetupLocked
    );
  }, [towerModels, hubType, hasDoubleSided, isSetupComplete, isExternalSetupLocked]);

  const uiSchema = useMemo(() => {
    return buildHubLayoutFormUISchema(hasDoubleSided);
  }, [hasDoubleSided]);

  const onSubmit = useCallback(
    async (formOutput: HubLayoutFormOutput) => {
      if (!hubID) return;
      const output = formOutput;
      if (isSetupComplete) {
        if (isExternalHub) {
          // TODO: Change this to the external lockers hub PATCH endpoint once implemented
          return;
        }
        await updateMutation.mutateAsync({
          hubID,
          dto: HubLayoutFormOutput.ToEditDto(output)
        });
      } else if (isExternalHub) {
        await setupExternalMutation.mutateAsync({
          dto: HubLayoutFormOutput.ToSetupExternalDto(output, hubID)
        });
        const kiosk = findKioskTower(output.towers, towerModels);
        if (kiosk) setSavedKioskState({ hubID, kiosk });
      } else {
        await setupMutation.mutateAsync({
          dto: HubLayoutFormOutput.ToSetupDto(output, hubID, towerModels)
        });
        const kiosk = findKioskTower(output.towers, towerModels);
        if (kiosk) setSavedKioskState({ hubID, kiosk });
      }
      // Update door position mappings (double-sided front/back names) via hub update
      const editHubDto = HubLayoutFormOutput.ToEditHubDto(output);
      if (editHubDto.doorPositionMappings) {
        await updateHubMutation.mutateAsync({ hubID, dto: editHubDto });
      }
      await hubDetailsResult.refetch();
    },
    [
      hubID,
      isSetupComplete,
      isExternalHub,
      towerModels,
      setupMutation,
      setupExternalMutation,
      updateMutation,
      updateHubMutation,
      hubDetailsResult,
      setSavedKioskState
    ]
  );

  // Composite model|inventoryID keys for all server-loaded towers.
  // Used to lock inventoryID edits on existing rows until the model is changed.
  const initialTowerKeys = useMemo(() => {
    return new Set(
      (initialTowers ?? []).filter((t) => t.model && t.inventoryID).map((t) => `${t.model}|${t.inventoryID}`)
    );
  }, [initialTowers]);

  const filteredTowerModels = useMemo(() => {
    const sides = formData?.sidesSelection ?? 'single';
    if (sides === 'double') return towerModels.filter((m) => m.isDoubleSided);
    return towerModels.filter((m) => !m.isDoubleSided);
  }, [towerModels, formData?.sidesSelection]);

  const onChangeInterceptor = useCallback(
    (currentFormData: HubLayoutForm, newFormData: HubLayoutForm): HubLayoutForm => {
      const prevSides = currentFormData?.sidesSelection;
      const nextSides = newFormData?.sidesSelection;
      if (prevSides !== nextSides && nextSides !== undefined && prevSides !== undefined) {
        const hasExistingTowers = (currentFormData?.towers ?? []).some((t) => t.model || t.inventoryID);
        if (hasExistingTowers) {
          setSidesWarning(
            nextSides === 'double'
              ? 'Switching to Double-Sided Lockers cleared your tower selection. Please re-select your tower models.'
              : 'Switching to Single-Sided Lockers cleared your tower selection. Please re-select your tower models.'
          );
          return { ...newFormData, towers: [{ model: '', inventoryID: '' }] };
        }
      }
      return newFormData;
    },
    [setSidesWarning]
  );

  const towersFieldContext = useMemo(
    () => ({
      towerModels: filteredTowerModels,
      lockedKiosk,
      isAllLocked: isExternalSetupLocked,
      initialTowerKeys,
      isSetupComplete,
      sidesWarning
    }),
    [filteredTowerModels, lockedKiosk, isExternalSetupLocked, initialTowerKeys, isSetupComplete, sidesWarning]
  );

  const customValidate: CustomValidator<HubLayoutForm> = useCallback(
    (formData, errors) => {
      const towers: HubLayoutTowerRow[] = formData?.towers ?? [];
      const kioskCount = towers.filter((t) => {
        const model = towerModels.find((m: TowerModelDto) => m.name === t.model);
        return model?.kioskPosition !== undefined;
      }).length;
      if (kioskCount === 0) {
        errors.towers?.addError('At least one tower must be a kiosk tower');
      } else if (kioskCount > 1) {
        errors.towers?.addError('Only one kiosk tower is allowed');
      }
      const inventoryIDs = towers.map((t) => t.inventoryID).filter(Boolean);
      const duplicates = inventoryIDs.filter((id, i) => inventoryIDs.indexOf(id) !== i);
      if (duplicates.length > 0) {
        errors.towers?.addError('Inventory IDs must be unique');
      }
      if (lockedKiosk) {
        const current = towers.find((t) => t.model === lockedKiosk.model && t.inventoryID === lockedKiosk.inventoryID);
        if (!current) {
          errors.towers?.addError('The kiosk tower cannot be changed after setup');
        }
      }
      return errors;
    },
    [towerModels, lockedKiosk]
  );

  const componentToRenderBelowForm = useCallback(
    (props: { formData: HubLayoutForm }) => (
      <HubLayoutPreview
        towers={props.formData?.towers ?? []}
        hubName={props.formData?.name}
        frontName={props.formData?.frontName}
        backName={props.formData?.backName}
        initialTowers={initialTowers}
        isEditMode={isSetupComplete}
        hubType={hubType}
      />
    ),
    [initialTowers, isSetupComplete, hubType]
  );

  if (!hubID) return null;

  return {
    isLoading: hubDetailsResult.isLoading || allTowerModelsResult.isLoading,
    title: 'Hub Layout',
    subtitle: hubID,
    allowSubtitleCopy: true,
    defaultValues,
    initialFormOutput,
    formSchema,
    uiSchema,
    fields: { towersEditor: HubLayoutTowersField },
    formContext: towersFieldContext,
    customValidate,
    onChangeInterceptor,
    onReset: () => setSidesWarning(null),
    getOutput(formData) {
      const towers = formData?.towers ?? [];
      return new HubLayoutFormOutput({
        manufacturer: formData.manufacturer,
        iotHubID: formData.iotHubID,
        towers,
        frontName: formData.frontName,
        backName: formData.backName
      });
    },
    onSubmit,
    componentToRenderBelowForm,
    isLocked: false,
    lockedMessage: null
  };
}
